export const BASE_CELL_SIZE = 44; // pixels per cell at 1.0x zoom
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 3.0;

export interface ViewportBounds {
  minCellX: number;
  maxCellX: number;
  minCellY: number;
  maxCellY: number;
  minBoxX: number;
  maxBoxX: number;
  minBoxY: number;
  maxBoxY: number;
}

export class Camera {
  public x: number;
  public y: number;
  public zoom: number;

  constructor(x: number = 0, y: number = 0, zoom: number = 1.0) {
    this.x = x;
    this.y = y;
    this.zoom = zoom;
  }

  public setZoom(newZoom: number): void {
    this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));
  }

  /**
   * Converts world cell (x, y) top-left corner to canvas pixel coordinates.
   */
  public worldToScreen(
    x: number,
    y: number,
    screenWidth: number,
    screenHeight: number
  ): { screenX: number; screenY: number; cellSize: number } {
    const cellSize = BASE_CELL_SIZE * this.zoom;
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    const screenX = centerX + (x * BASE_CELL_SIZE - this.x) * this.zoom;
    const screenY = centerY + (y * BASE_CELL_SIZE - this.y) * this.zoom;

    return { screenX, screenY, cellSize };
  }

  /**
   * Converts screen pixel (screenX, screenY) to world integer cell coordinate (x, y).
   */
  public screenToWorld(
    screenX: number,
    screenHeightX: number,
    screenWidth: number,
    screenHeight: number
  ): { x: number; y: number } {
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    const worldPxX = (screenX - centerX) / this.zoom + this.x;
    const worldPxY = (screenHeightX - centerY) / this.zoom + this.y;

    const x = Math.floor(worldPxX / BASE_CELL_SIZE);
    const y = Math.floor(worldPxY / BASE_CELL_SIZE);

    return { x, y };
  }

  /**
   * Calculates visible cell and box ranges including 1-box buffer padding.
   */
  public getViewportBounds(screenWidth: number, screenHeight: number): ViewportBounds {
    const topLeft = this.screenToWorld(0, 0, screenWidth, screenHeight);
    const bottomRight = this.screenToWorld(screenWidth, screenHeight, screenWidth, screenHeight);

    // Buffer of 10 cells (1 box) around screen
    const BUFFER = 10;
    const minCellX = topLeft.x - BUFFER;
    const minCellY = topLeft.y - BUFFER;
    const maxCellX = bottomRight.x + BUFFER;
    const maxCellY = bottomRight.y + BUFFER;

    const minBoxX = Math.floor(minCellX / 10);
    const maxBoxX = Math.floor(maxCellX / 10);
    const minBoxY = Math.floor(minCellY / 10);
    const maxBoxY = Math.floor(maxCellY / 10);

    return {
      minCellX,
      maxCellX,
      minCellY,
      maxCellY,
      minBoxX,
      maxBoxX,
      minBoxY,
      maxBoxY,
    };
  }
}
